// @ts-check
import { getQuotaPricePath } from 'utility/redux/selectorHelperFunctions/backups';
import { findOption, findOptionValue } from 'utility/products/options';
import { perMonth } from 'utility/format';

const backupsDisplayMap = {
	None: () => 'None',
	/** @param {{ price: number }} params */
	CloudDaily: ({ price }) => `Pay per GB (${perMonth(price)} per GB)`,
	/** @param {{ price: number }} params */
	Daily: ({ price }) => `Pay per GB (${perMonth(price)} per GB)`,
	Quota: () => 'Quota Pricing',
	Acronis: () => 'Acronis Backups',
};

/**
 * @param {string} backupPlan
 * */
export const getType = (backupPlan) => {
	switch (backupPlan) {
		case 'BackupDay':
		case 'CloudDaily':
		case 'Daily':
			return 'Daily';
		case 'BackupQuota':
		case 'Quota':
			return 'Quota';
		default:
			return 'None';
	}
};

/**
 * @param {import('club-sauce/public/product/index.raw').LWApiPublicProductDetailsResultOptionRawI['values']} backupPlans
 * @param {import('modules/queries/asset/useDetails.js').BackupsState} backupsState
 * */
const getOptions = (backupPlans, backupsState) => {
	const noBackupOption = [{ value: 'None', label: backupsDisplayMap.None() }];
	const legacyBackupPlans =
		backupPlans?.map(({ value }) => {
			const path = getQuotaPricePath(value);
			const daily =
				findOptionValue('Daily', backupPlans)?.options ||
				findOptionValue('CloudDaily', backupPlans)?.options;
			const option = findOption(path, daily);
			const price = option?.values[0]?.prices['1']?.month;
			return {
				value,
				label: backupsDisplayMap[
					/** @type {keyof typeof backupsDisplayMap} */ (value)
				]?.({ price }),
			};
		}) || [];
	const currentLegacyBackupPlan = legacyBackupPlans?.filter(
		({ value }) => getType(value) === backupsState.currentPlan,
	);
	const acronisBackupOption = [
		{ value: 'AcronisBackup', label: backupsDisplayMap.Acronis() },
	];

	if (!backupsState.canAcronis) {
		// Unable to use Acronis, need to show all legacy options
		// legacyBackupPlans includes the "Disable" option
		return legacyBackupPlans.length ? legacyBackupPlans : noBackupOption;
	}

	if (!backupsState.hasLegacyBackups) {
		// canAcronis and doesn't have legacy, can only use Acronis going forward
		return [...noBackupOption, ...acronisBackupOption];
	}

	// Everyone else would have a current legacy plan, let that be choosable, or they can switch to Disable or Acronis
	return [
		...noBackupOption,
		...currentLegacyBackupPlan,
		...acronisBackupOption,
	];
};

export default getOptions;
